import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, X } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { fmtDate, fmtDateOd, statusMeta, TONE_CLASS, usedFraction, useMyPlan } from "@/lib/planDisplay";

export const PlanStatusChip = ({ plan, testId = "plan-status-chip" }) => {
  const m = statusMeta(plan);
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TONE_CLASS[m.tone]}`} data-testid={testId} data-tone={m.tone}>
      {m.label} · {m.od}
    </span>
  );
};

// Small header chip, e.g. "Yearly MDCF Premium · 23 days left".
export const PlanMiniChip = ({ testId = "plan-mini-chip", className = "" }) => {
  const { user } = useAuth();
  const plan = useMyPlan(user?.role === "school" ? user.id : null);
  if (!plan || !plan.planLabel || plan.status === "none") return null;
  const m = statusMeta(plan);
  const tail = plan.status === "active" || plan.status === "expiring_soon" ? `${plan.daysLeft}d left` : m.label;
  return (
    <Link to="/my-plan" className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[0.7rem] font-semibold transition-colors hover:opacity-80 ${TONE_CLASS[m.tone]} ${className}`} data-testid={testId}>
      <span className="truncate">{plan.planLabel.split(" ")[0]} · {tail}</span>
    </Link>
  );
};

export const MyPlanCard = () => {
  const { user } = useAuth();
  const plan = useMyPlan(user?.role === "school" ? user.id : null);
  if (!plan) return null;
  const used = usedFraction(plan);
  const urgent = plan.status === "expiring_soon" || plan.status === "expired";
  return (
    <section className="mb-6 rounded-2xl border border-border bg-white p-4 shadow-[0_1px_0_rgba(44,53,40,0.04)] sm:p-5" data-testid="my-plan-card">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-ink-700">My Plan · ମୋ ପ୍ଲାନ</p>
          <h2 className="mt-1 truncate font-heading text-lg font-bold text-ink-900" data-testid="my-plan-card-name">{plan.planLabel || "No active plan · କୌଣସି ପ୍ଲାନ ନାହିଁ"}</h2>
        </div>
        <PlanStatusChip plan={plan} testId="my-plan-card-status" />
      </div>
      {plan.activeUntil && (
        <p className="mt-2 text-sm text-ink-700" data-testid="my-plan-card-valid">
          <CalendarClock className="mr-1 inline h-4 w-4" />
          {plan.status === "expired" ? "Expired on" : "Valid till"} <b>{fmtDate(plan.activeUntil)}</b>
          <span className="ml-1 text-xs">({fmtDateOd(plan.activeUntil)})</span>
          {plan.daysLeft > 0 && <span className="ml-2 font-semibold text-ink-900" data-testid="my-plan-card-days">{plan.daysLeft} days left · {plan.daysLeft} ଦିନ ବାକି</span>}
        </p>
      )}
      {used !== null && (
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-sand-100" data-testid="my-plan-card-progress">
          <div className="h-full rounded-full bg-moss transition-[width] duration-700" style={{ width: `${Math.round(used * 100)}%` }} />
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <Link to="/my-plan" className="rounded-full border border-border px-4 py-1.5 text-sm font-semibold text-ink-900 transition-colors hover:bg-sand-100" data-testid="my-plan-card-details">View details · ବିବରଣୀ</Link>
        <Link to="/choose-plan" className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${urgent ? "bg-clay text-white hover:bg-clay-dark" : "border border-border text-ink-700 hover:bg-sand-100"}`} data-testid="my-plan-card-renew">
          {urgent ? "Renew now · ନବୀକରଣ" : "Renew / Upgrade"}
        </Link>
      </div>
    </section>
  );
};

const REMIND_DAYS = new Set([7, 3, 1, 0]);
// In-app reminder at 7 / 3 / 1 days before expiry and on the expiry day; dismissible, once per day.
export const PlanExpiryBanner = () => {
  const { user } = useAuth();
  const plan = useMyPlan(user?.role === "school" ? user.id : null);
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const key = `mdm.planReminder.${user?.id}`;
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem(key) === today; } catch { return false; } });
  if (!plan?.activeUntil || hidden) return null;
  const endDay = new Date(plan.activeUntil).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const d = Math.round((Date.parse(endDay) - Date.parse(today)) / 86400000);
  if (!REMIND_DAYS.has(d)) return null;
  const dismiss = () => { try { localStorage.setItem(key, today); } catch { /* ignore */ } setHidden(true); };
  return (
    <div className="mb-4 flex items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status" data-testid="plan-expiry-banner">
      <span className="flex-1">
        {d === 0 ? "Your plan ends today" : `Your plan ends on ${fmtDate(plan.activeUntil)}`} · ଆପଣଙ୍କ ପ୍ଲାନ {fmtDateOd(plan.activeUntil)} ରେ ଶେଷ ହେବ
      </span>
      <Link to="/choose-plan" className="rounded-full bg-clay px-3 py-1 text-xs font-semibold text-white" data-testid="plan-expiry-banner-renew">Renew</Link>
      <button type="button" onClick={dismiss} aria-label="Dismiss" className="text-amber-800 hover:text-amber-950" data-testid="plan-expiry-banner-dismiss"><X className="h-4 w-4" /></button>
    </div>
  );
};
